import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPrice, formatQty } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderItems = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [orderId]);

  const fetchData = async () => {
    try {
      const [orderRes, itemsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/orders/${orderId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/order-items/${orderId}`, { withCredentials: true })
      ]);
      setOrder(orderRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to load order items');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderItemId, newStatus) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/order-items/${orderItemId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Pending': 'bg-stone-100 text-stone-800 border-stone-200',
      'Delivered': 'bg-lime-100 text-lime-800 border-lime-200',
      'No Stock': 'bg-red-100 text-red-800 border-red-200',
      'Ordered from Vendor': 'bg-amber-100 text-amber-800 border-amber-200',
      'Dispatched from Vendor': 'bg-amber-100 text-amber-800 border-amber-200',
      'Received in Store': 'bg-amber-100 text-amber-800 border-amber-200',
      'Ready for Delivery': 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || styles['Pending']}`}>
        {status}
      </span>
    );
  };

  const statusOptions = [
    'Pending',
    'Delivered',
    'No Stock',
    'Ordered from Vendor',
    'Dispatched from Vendor',
    'Received in Store',
    'Ready for Delivery'
  ];

  if (loading) {
    return <div className="text-stone-600">Loading order items...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <Button
          data-testid="back-to-orders-btn"
          onClick={() => navigate('/admin/orders')}
          variant="outline"
          className="mb-4 border-stone-200 text-stone-700 hover:bg-stone-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Orders
        </Button>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Order Items - {orderId}
        </h1>
        <p className="mt-2 text-base text-stone-600">
          Track and manage item-level status for this order
        </p>
        {order && (
          <div className="mt-4 flex gap-4 text-sm">
            <div>
              <span className="text-stone-500">Order Date:</span>
              <span className="ml-2 font-medium text-stone-900">{order.order_date}</span>
            </div>
            <div>
              <span className="text-stone-500">Total Amount:</span>
              <span className="ml-2 font-medium text-stone-900">₹{formatPrice(order.total_amount)}</span>
            </div>
            <div>
              <span className="text-stone-500">Order Status:</span>
              <span className="ml-2">{getStatusBadge(order.status)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Item ID</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Product</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Quantity</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Price</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Stock Available</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Remarks</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Status</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Update Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {items.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-stone-500">
                    No items found for this order.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.order_item_id} data-testid={`item-row-${item.order_item_id}`} className="hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-stone-900 text-xs">{item.order_item_id}</td>
                    <td className="px-6 py-4 text-stone-700">
                      <div className="font-medium">{item.product_name} - {item.product_brand}</div>
                      <div className="text-xs text-stone-500">{item.product_size} - {item.product_color}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-stone-900 tabular-nums">{formatQty(item.quantity)}</td>
                    <td className="px-6 py-4 text-right text-stone-900 font-medium tabular-nums">₹{formatPrice(item.price)}</td>
                    <td className="px-6 py-4 text-right tabular-nums">
                      <span className={item.available_stock_quantity >= item.quantity ? 'text-lime-700 font-medium' : 'text-red-700 font-medium'}>
                        {formatQty(item.available_stock_quantity)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-stone-700">{item.remarks || '-'}</td>
                    <td className="px-6 py-4">{getStatusBadge(item.status)}</td>
                    <td className="px-6 py-4">
                      <Select
                        value={item.status}
                        onValueChange={(value) => handleStatusChange(item.order_item_id, value)}
                      >
                        <SelectTrigger data-testid={`status-select-${item.order_item_id}`} className="w-[180px] border-stone-200 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((status) => (
                            <SelectItem key={status} value={status} className="text-xs">
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 p-6">
        <h2 className="text-lg font-medium text-stone-900 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Status Workflow Guide
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="space-y-1">
            <div className="font-medium text-stone-900">1. Initial State</div>
            <div className="text-xs text-stone-600">Pending / Delivered / No Stock</div>
          </div>
          <div className="space-y-1">
            <div className="font-medium text-stone-900">2. Vendor Process</div>
            <div className="text-xs text-stone-600">Ordered from Vendor → Dispatched</div>
          </div>
          <div className="space-y-1">
            <div className="font-medium text-stone-900">3. Store Receipt</div>
            <div className="text-xs text-stone-600">Received in Store</div>
          </div>
          <div className="space-y-1">
            <div className="font-medium text-stone-900">4. Final State</div>
            <div className="text-xs text-stone-600">Ready for Delivery → Delivered</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderItems;